import { Link } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import useMyContests from "@/hooks/useMyContests";
import { clearAuth, logout } from "@/lib/authApi";
import { LogOut, LogIn, Trophy } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_fairway-picks/artifacts/hbhwifnu_ChatGPT%20Image%20Jun%208%2C%202026%2C%2001_54_26%20PM.png";

export const PBOLogo = ({ size = 96, className = "" }) => (
  <img
    src={LOGO_URL}
    alt="The Palm Breezy Open"
    width={size}
    height={size}
    className={className}
    style={{ width: size, height: size, objectFit: "contain" }}
    data-testid="pbo-logo"
  />
);

const AuthPill = () => {
  const { user, isSignedIn } = useAuth();
  const contests = useMyContests();
  const count = contests.length;
  const doLogout = async () => {
    await logout();
    clearAuth();
  };
  if (isSignedIn) {
    return (
      <div
        className="flex items-center gap-1.5 text-xs"
        data-testid="auth-signed-in"
      >
        <Link
          to="/my-contests"
          className={`inline-flex items-center gap-1.5 font-semibold px-3 py-1.5 rounded-full transition-colors ${
            count > 0
              ? "bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft shadow-sm"
              : "bg-pbo-gold/15 text-pbo-gold border border-pbo-gold/50 hover:bg-pbo-gold hover:text-pbo-ink"
          }`}
          data-testid="my-contests-link"
        >
          <Trophy className="w-3.5 h-3.5" />
          My contests
          {count > 0 && (
            <span
              className="bg-pbo-green text-pbo-cream rounded-full text-[10px] min-w-[18px] h-[18px] grid place-items-center px-1 font-bold"
              data-testid="my-contests-count"
            >
              {count}
            </span>
          )}
        </Link>
        <span
          className="hidden sm:inline text-pbo-muted truncate max-w-[180px]"
          title={user.email}
        >
          {user.email}
        </span>
        <button
          type="button"
          onClick={doLogout}
          className="text-pbo-muted hover:text-red-600 transition-colors px-2 py-1 rounded"
          title="Sign out"
          data-testid="auth-logout-btn"
        >
          <LogOut className="w-3.5 h-3.5 inline sm:mr-1" />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    );
  }
  return (
    <Link
      to="/login"
      className="text-xs text-pbo-green hover:text-pbo-green-deep font-semibold px-2 py-1 rounded flex items-center gap-1"
      data-testid="auth-signin-link"
    >
      <LogIn className="w-3.5 h-3.5" />
      Sign in
    </Link>
  );
};

export const SiteHeader = ({ rightSlot = null }) => (
  <header
    className="border-b border-pbo-border/70 bg-pbo-cream/80 backdrop-blur sticky top-0 z-30"
    data-testid="site-header"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
      <Link
        to="/"
        className="flex items-center gap-3 group"
        data-testid="home-link"
      >
        <PBOLogo size={44} />
        <div className="leading-tight hidden sm:block">
          <div className="font-serif text-pbo-green font-semibold text-lg tracking-tight">
            The Palm Breezy Open
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-pbo-gold font-semibold">
            Auction Draft Room
          </div>
        </div>
      </Link>
      <div className="flex items-center gap-3">
        {rightSlot}
        <AuthPill />
      </div>
    </div>
  </header>
);
